import { Host, h } from "@stencil/core";
const mapNotificationsAlignToChWindowAlign = {
    OutsideStart: "outside-start",
    Center: "center",
    OutsideEnd: "outside-end"
};
export class ChNotifications {
    constructor() {
        this.notificationsSize = 0;
        this.delayToAnimateNewNotifications = 50;
        this.notifications = [];
        this.position = "Center_OutsideEnd";
        this.timeToDismissNotifications = 5000;
        this.timeType = "Milliseconds";
    }
    /**
     * `true` if the componentDidLoad lifecycle method was executed
     */
    didLoad = false;
    /**
     * Saves the last notification ID
     */
    lastNotificationID = 0;
    /**
     * Notifications to be shown by the component loop
     */
    currentNotifications = new Map();
    handleNewNotificationsChange(newNotifications) {
        // Sometimes StencilJS calls this decorator before the component did load
        if (this.didLoad) {
            this.addNewNotifications(newNotifications);
        }
    }
    handleNotificationClick(event) {
        const notification = this.currentNotifications.get(event.detail);
        if (!notification) {
            return;
        }
        if (notification.closeOnClick) {
            this.currentNotifications.delete(event.detail);
            // Trigger render
            this.notificationsSize--;
        }
    }
    handleNotificationDismiss(event) {
        this.currentNotifications.delete(event.detail);
        // Trigger render
        this.notificationsSize--;
    }
    addNewNotifications(notifications) {
        if (!notifications) {
            return;
        }
        let delayToAnimate = 0;
        notifications.forEach(notification => {
            const notificationID = notification.Id ?? this.lastNotificationID;
            // If the notification did not have an ID, increment the counter
            if (!notification.Id) {
                this.lastNotificationID++;
            }
            notification.Id = notificationID;
            notification.delayToAnimate = delayToAnimate;
            this.currentNotifications.set(notificationID, notification);
            delayToAnimate++;
        });
    }
    getMessages = () => [...this.currentNotifications.values()];
    getTimeToDismiss = () => this.timeType === "Seconds"
        ? this.timeToDismissNotifications * 1000
        : this.timeToDismissNotifications;
    componentWillLoad() {
        this.addNewNotifications(this.notifications);
    }
    componentDidLoad() {
        this.didLoad = true;
    }
    render() {
        const messages = this.getMessages();
        const aligns = this.position.split("_");
        const alignX = aligns[0];
        const alignY = aligns[1];
        return (h(Host, { key: '02b3ffbfcf2d158fc2410fb403f98c7e49361d9a', tabindex: messages.length > 0 ? "0" : undefined, role: "alert", "aria-atomic": "true", "x-align": mapNotificationsAlignToChWindowAlign[alignX], "y-align": mapNotificationsAlignToChWindowAlign[alignY] }, messages.map(({ Id, Value, Class, delayToAnimate, timerInterval, dismissTimeout }) => (h("ch-notifications-item", { id: Id.toString(), key: Id, class: Class, "timer-interval": timerInterval, style: {
                "--delay-to-animate": `${delayToAnimate * this.delayToAnimateNewNotifications}ms`
            }, "dismiss-timeout": !dismissTimeout
                ? this.getTimeToDismiss() +
                    delayToAnimate * this.delayToAnimateNewNotifications
                : dismissTimeout, "show-close-button": true, "show-timeout-bar": true, "pause-on-hover": true }, h("div", { slot: "content" }, Value))))));
    }
    static get is() { return "ch-notifications"; }
    static get originalStyleUrls() {
        return {
            "$": ["notifications.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["notifications.css"]
        };
    }
    static get properties() {
        return {
            "delayToAnimateNewNotifications": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Delay to animate new notifications"
                },
                "attribute": "delay-to-animate-new-notifications",
                "reflect": false,
                "defaultValue": "50"
            },
            "notifications": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "NotificationMessageWithDelay[]",
                    "resolved": "NotificationMessageWithDelay[]",
                    "references": {
                        "NotificationMessageWithDelay": {
                            "location": "import",
                            "path": "./notifications-types",
                            "id": "src/components/notifications/notifications-types.ts::NotificationMessageWithDelay"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The notifications prop"
                },
                "defaultValue": "[]"
            },
            "position": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "NotificationsPositions",
                    "resolved": "\"Center_OutsideEnd\" | \"Center_OutsideStart\" | \"OutsideEnd_OutsideEnd\" | \"OutsideEnd_OutsideStart\" | \"OutsideStart_OutsideEnd\" | \"OutsideStart_OutsideStart\"",
                    "references": {
                        "NotificationsPositions": {
                            "location": "import",
                            "path": "./notifications-types",
                            "id": "src/components/notifications/notifications-types.ts::NotificationsPositions"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "/**\n  Specifies the position of the whole notifications section \n  that is placed relative to the window."
                },
                "attribute": "position",
                "reflect": false,
                "defaultValue": "\"Center_OutsideEnd\""
            },
            "timeToDismissNotifications": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The default dismiss timeout as group\nif not set for each notification individually"
                },
                "attribute": "time-to-dismiss-notifications",
                "reflect": false,
                "defaultValue": "5000"
            },
            "timeType": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"Seconds\" | \"Milliseconds\"",
                    "resolved": "\"Milliseconds\" | \"Seconds\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Time type only applies for timeToDismissNotifications,\nnot for individual dismissTimeout"
                },
                "attribute": "time-type",
                "reflect": false,
                "defaultValue": "\"Milliseconds\""
            }
        };
    }
    static get states() {
        return {
            "notificationsSize": {}
        };
    }
    static get watchers() {
        return [{
                "propName": "notifications",
                "methodName": "handleNewNotificationsChange"
            }];
    }
    static get listeners() {
        return [{
                "name": "notificationClick",
                "method": "handleNotificationClick",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "notificationDismiss",
                "method": "handleNotificationDismiss",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
